import { Building2, Loader2, TrendingUp, UserCheck, Users } from "lucide-react";
import { useEffect, useState } from "react";
import { Card, CardContent } from "../../components/ui/Card.jsx";
import { fetchDashboard } from "../../services/superAdminService.js";

export default function SuperAdminDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchDashboard();
        setData(res.data);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!data) return <p className="text-gray-500">Failed to load dashboard.</p>;

  const stats = [
    { label: "Total Users", value: data.total_users, icon: Users, color: "text-blue-600 bg-blue-50" },
    { label: "Total Companies", value: data.total_companies, icon: Building2, color: "text-purple-600 bg-purple-50" },
    { label: "Total Leads", value: data.total_leads, icon: TrendingUp, color: "text-green-600 bg-green-50" },
    { label: "Active Users (30d)", value: data.active_users, icon: UserCheck, color: "text-teal-600 bg-teal-50" },
  ];

  return (
    <div className="space-y-6">
      <h1 className="text-xl font-bold text-gray-900">Dashboard</h1>

      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((s) => (
          <Card key={s.label} className="border-gray-100">
            <CardContent className="p-4 flex items-center gap-3">
              <div className={`p-2 rounded-lg ${s.color}`}>
                <s.icon size={18} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{s.value}</p>
                <p className="text-xs text-gray-500">{s.label}</p>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="border-gray-100">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">User Signups (Monthly)</h3>
            <div className="space-y-1.5">
              {data.signup_trend.map((item) => (
                <div key={item.month} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-16">{item.month}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                    <div
                      className="bg-blue-400 h-full rounded-full transition-all"
                      style={{
                        width: `${Math.max(4, (item.count / Math.max(...data.signup_trend.map((i) => i.count), 1)) * 100)}%`,
                      }}
                    />
                  </div>
                  <span className="text-xs font-medium text-gray-700 w-8 text-right">{item.count}</span>
                </div>
              ))}
              {data.signup_trend.length === 0 && (
                <p className="text-xs text-gray-400">No data yet</p>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="border-gray-100">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-gray-700 mb-3">Leads Created (Monthly)</h3>
            <div className="space-y-1.5">
              {data.lead_trend.map((item) => (
                <div key={item.month} className="flex items-center gap-2">
                  <span className="text-xs text-gray-500 w-16">{item.month}</span>
                  <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden">
                    <div
                      className="bg-green-400 h-full rounded-full transition-all"
                      style={{
                        width: `${Math.max(4, (item.count / Math.max(...data.lead_trend.map((i) => i.count), 1)) * 100)}%`,
                      }}
                    />
                  </div>
                  <span className="text-xs font-medium text-gray-700 w-8 text-right">{item.count}</span>
                </div>
              ))}
              {data.lead_trend.length === 0 && (
                <p className="text-xs text-gray-400">No data yet</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      <Card className="border-gray-100">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Recent Activity</h3>
          <div className="space-y-2">
            {data.recent_activity.map((log, idx) => {
              let meta = null;
              try { meta = typeof log.metadata === "string" ? JSON.parse(log.metadata) : log.metadata; } catch {}
              return (
              <div
                key={idx}
                className="flex items-center justify-between py-2 border-b border-gray-50 last:border-0"
              >
                <div className="flex items-center gap-3">
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 font-medium uppercase">
                    {log.event_type}
                  </span>
                  <span className="text-xs text-gray-700">
                    {log.user_name
                      ? <>{log.user_name} <span className="text-gray-400">({log.user_email})</span></>
                      : meta?.source === "facebook_ads"
                        ? <span className="text-blue-600">Facebook Ads</span>
                        : <>Unknown <span className="text-gray-400">({log.user_email})</span></>
                    }
                  </span>
                </div>
                <div className="text-[10px] text-gray-400 flex items-center gap-2">
                  {log.ip_address && <span>{log.ip_address}</span>}
                  <span>{new Date(log.created_at).toLocaleString()}</span>
                </div>
              </div>
              );
            })}
            {data.recent_activity.length === 0 && (
              <p className="text-xs text-gray-400">No activity yet</p>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
